import React, {FC, useMemo} from 'react';
import {ModerationUser, ModerationUserTag} from "interfaces";
import {getStyleForUserReviewTag, isReviewTagPresent, ReviewTagInfoTypes, RootRoutes, UserReviewTags} from "enums";
import {toDateString, useImagePreview} from "utils";
import {getGuid} from "utils/getGuid";
import {ModerationChatModal} from "components/ModerationUserCard/components/ModerationChatModal";
import {getDuplicateUsers, getPhotoInfos, PHOTO_LOCATION_DESCRIPTIONS, PhotoLocation} from "./helpers";
import {ModerationUserActors} from "components/ModerationUserCard/components/ModerationUserActors";


interface ModerationTagProps {
    tag: ModerationUserTag;
    user: ModerationUser;
}

export const ModerationTag: FC<ModerationTagProps> = ({tag, user}) => {
    const imagePreview = useImagePreview();

    const photoInfos = useMemo(() => getPhotoInfos(tag, user), [tag, user]);
    const duplicateUsers = useMemo(() => getDuplicateUsers(tag, user), [tag, user]);

    const handleImageClick = (imageLink: string) => {
        imagePreview.open(imageLink, false);
    };

    const handleOpenProfile = (e: React.MouseEvent) => {
        e.preventDefault();
        window.open(`${RootRoutes.Profile}/${tag.infoValue}`, '_blank', 'rel=noopener noreferrer');
        return false;
    };


    const renderInfoText = () => {
        if (photoInfos?.length) {
            return null;
        }


        return (
            <div className="p-0 text-black mt-1">
                {tag.info?.split('\n').map((m, i) => {
                    const guid = getGuid(m);
                    const isCurrent = user.id === guid;

                    if (isCurrent) return null;

                    return (
                        <div key={m + i}>
                            {guid ? (
                                <a href={`profile/${guid}`} target="_blank" rel="noreferrer">
                                    Пользователь {i + 1}
                                </a>
                            ) : (
                                m
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderPhotos = () => {
        if (!photoInfos?.length) {
            return null;
        }

        return <div>
            <div className="d-flex flex-row mt-1 mb-1" style={{gap: '6px'}}>
                {tag.tag !== UserReviewTags.DuplicateFacePhotos && photoInfos.map(photoInfo => {
                    if (!photoInfo.photo?.image?.length) {
                        if (tag.tag === UserReviewTags.DuplicateFacePhotos) {
                            return null;
                        }
                        return <div key={photoInfo.key}
                                    className="d-flex justify-content-center align-items-center p-2 rounded border border-dark mt-1"
                                    style={{height: 100, width: 100}}>
                            <p className="small text-center">Фото не доступно...</p>
                        </div>
                    }

                    const renderPhotoInfo = (key?: string, pi?: string) => {
                        if (tag.tag === UserReviewTags.UpdatedPhotos || !key || !pi) {
                            return null;
                        }

                        if (tag.tag === UserReviewTags.CelebrityPhoto) {
                            try {
                                const infoObj = JSON.parse(pi) as Array<{
                                    Name: string,
                                    Urls: string[],
                                    MatchConfidence: number
                                }>;
                                return <div className="mb-2 small">{
                                    infoObj.map(i =>
                                        <div key={i.Name}>
                                            <span>{i.Name}</span>
                                            <br/>
                                            {i.Urls.map(u => {
                                                const href = /^https?:\/\//i.test(u) ? u : `https://${u}`;
                                                return <a key={u} href={href} target="_blank" rel="noreferrer">{u}</a>;
                                            })}
                                        </div>)
                                }</div>
                            } catch (err) {
                                return null
                            }
                        }

                        const infos = pi?.split(/\n(?:Abuse reason|Labels|Age ranges|QR text):/);
                        const info = infos ? infos.length > 1 ? infos[1] : infos[0] : undefined
                        const stringInfo = !!info?.indexOf(key ?? '') ? info : null;
                        return stringInfo && <div className="mb-2 small">{stringInfo}</div>
                    }

                    const image = photoInfo.photo.image[0];


                    return (
                        <div key={photoInfo.key} className="p-0 mt-1">
                            {renderPhotoInfo(photoInfo?.key, tag?.info)}
                            <img
                                src={image.link}
                                alt={image.link}
                                height={100}
                                width={100}
                                className="rounded border border-dark"
                                style={{
                                    objectFit: 'cover',
                                    opacity: photoInfo.location === PhotoLocation.Archive ? 0.5 : 1
                                }}
                                onClick={() => handleImageClick(image.link)}
                            />
                        </div>
                    );
                })}
            </div>
            <ModerationUserActors users={duplicateUsers}/>
        </div>
    };

    const renderBlackListLink = () => {
        if (tag.infoType !== ReviewTagInfoTypes.BlackList) return null;

        return (
            <a href="/" onClick={handleOpenProfile}>
                Пользователь из черного списка
            </a>
        );
    };

    const renderBannedUser = () => {
        if (tag.infoType !== ReviewTagInfoTypes.BannedUser) return null;

        return (
            <a href="/" onClick={handleOpenProfile}>
                Заблокированный пользователь
            </a>
        );
    };

    const renderChatModal = () => {
        if (tag.infoType !== ReviewTagInfoTypes.ChatUser || !isReviewTagPresent(tag.infoState)) {
            return null;
        }

        return (
            <ModerationChatModal
                userKey={user.id}
                userName={user.name}
                userGender={user.gender}
                tag={tag}
            />
        );
    };

    const style = getStyleForUserReviewTag(tag.tag);

    const photoLocations = useMemo(
        () =>
            Array.from(
                new Set(
                    (photoInfos ?? [])
                        .map(p => p.location)
                        .filter((loc): loc is keyof typeof PHOTO_LOCATION_DESCRIPTIONS => !!loc)
                )
            ),
        [photoInfos]
    );
    return (
        <div className="container row my-2">
            <div className="col-12 col-sm-6 col-md-4 g-0 text-wrap">
                {tag.date && <span className="mr-1">{toDateString(tag.date, true)}</span>}
            </div>
            <div className="col-12 col-sm-6 col-md-8 g-0 pt-1">
                <div className="d-flex flex-row align-items-center" style={{gap: '6px'}}>
                    <span className={`badge ${style.bg} ${style.text}`} style={style.style}>
                        #{tag.tag}
                    </span>

                    {tag.tag !== UserReviewTags.DuplicateFacePhotos && photoLocations.length > 0 && (
                        <span className="badge bg-secondary text-black">
                            {photoLocations
                                .map(location => PHOTO_LOCATION_DESCRIPTIONS[location])
                                .join(', ')}
                        </span>
                    )}

                    {renderChatModal()}
                </div>

                {renderInfoText()}
                {renderPhotos()}

                {renderBlackListLink()}
                {renderBannedUser()}
            </div>
        </div>
    );
};




